/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IAddressAddItem,
    IAddressAddUpdateProps
} from '@msdyn365-commerce-modules/address/src/modules/account-management-address/../../common/components/address-add';
import {
    IAddressList,
    IAddressListItem,
    IAddressListProps
} from '@msdyn365-commerce-modules/address/src/modules/account-management-address/../../common/components/address-list';
import {
    IAddressShowItem,
    IAddressShowProps
} from '@msdyn365-commerce-modules/address/src/modules/account-management-address/../../common/components/address-show';
import { IAccountManagementAddressViewProps } from './b2b-account-management-address';
// import { IAccountManagementAddressViewProps } from '@msdyn365-commerce-modules/address/src/modules/account-management-address/./account-management-address';
// import { AddressDetailItemComponent } from '@msdyn365-commerce-modules/address/src/common/components/address-detail-item';

interface IAddressListPropsExt extends IAddressListProps {
    shouldRenderEditAddressButton: boolean;
}

interface IAddressListExt extends IAddressList {
    shouldRenderEditAddressButton: boolean;
}

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem) => {
                return <>{item.description}</>;
            })}
        </Node>
    );
};

/**
 * Address List Component.
 * @param param0 - Root param.
 * @param param0.List - Address list.
 * @param param0.isShowList - Boolean.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressListItem[].
 * @returns - AddressListComponent Node.
 */
const AddressList: React.FC<IAddressListExt> = ({ List, isShowList, heading, items, shouldRenderEditAddressButton }) => {
    if (!isShowList) {
        return null;
    }

    return (
        <Node {...List}>
            {heading}
            {items.map((item: IAddressListItem) => {
                const { Item, key, error, showItems, isShowPrimaryButton, primaryButton, editButton, removeButton } = item;
                const { Error, isShowError, title, message } = error;

                // const component = showItems.items.find(address => address.key === "AddressTypeValue")?.description;

                return (
                    <Node {...Item} key={key}>
                        <AddressShow {...showItems} />
                        {isShowError && (
                            <Node {...Error}>
                                {title}
                                {message}
                            </Node>
                        )}
                        {isShowPrimaryButton && primaryButton}
                        <div className={`${shouldRenderEditAddressButton ? '' : 'no-edit-button'}`}>
                            {shouldRenderEditAddressButton && editButton}
                            {removeButton}
                        </div>
                    </Node>
                );
            })}
        </Node>
    );
};

/**
 * Address Lists Component.
 * @param param0 - Root param.
 * @param param0.ListAddress - List address.
 * @param param0.heading - List heading.
 * @param param0.isShowEmptyListMessage - Boolean.
 * @param param0.emptyListMessage - Empty list message.
 * @param param0.addButton - Add button.
 * @param param0.primaryAddressList - Primary address.
 * @param param0.otherAddressList - Other address.
 * @returns Address Lists Component Node.
 */
const AddressLists: React.FC<IAddressListPropsExt> = ({
    ListAddress,
    heading,
    isShowEmptyListMessage,
    emptyListMessage,
    addButton,
    primaryAddressList,
    otherAddressList,
    shouldRenderEditAddressButton
}) => {
    return (
        <Node {...ListAddress}>
            {heading}
            {isShowEmptyListMessage && emptyListMessage}
            {addButton}
            <AddressList {...primaryAddressList} shouldRenderEditAddressButton={shouldRenderEditAddressButton} />
            <AddressList {...otherAddressList} shouldRenderEditAddressButton={shouldRenderEditAddressButton} />
        </Node>
    );
};

/**
 * AddressAddUpdateComponent.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                return (
                    <Node {...AddressItem} key={key}>
                        {label}
                        {alert}
                        {input}
                    </Node>
                );
            })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Account Management Address View Component.
 * @param props - Props.
 * @returns - AccountAddressManagement Module.
 */
const AccountManagementAddressView: React.FC<IAccountManagementAddressViewProps> = props => {
    const {
        AccountAddressManagement,
        viewState,
        showAddressList,
        showAddOrUpdateAddress,
        screenReaderNotification,
        shouldRenderEditAddressButton
    } = props;

    console.log(shouldRenderEditAddressButton);
    // Remove buttons from Invoice address
    const invoiceAddressId = 1;
    const addressId = props.customerAddresses.slice().find(address => address.AddressTypeValue === invoiceAddressId)?.RecordId;
    const invoiceAddress = showAddressList.otherAddressList.items.find(address => address.key === addressId);
    if (invoiceAddress) {
        invoiceAddress.editButton = undefined;
        invoiceAddress.primaryButton = undefined;
        invoiceAddress.removeButton = undefined;
    }

    return (
        <Module {...AccountAddressManagement}>
            {viewState.isShowAddresList && (
                <AddressLists {...showAddressList} shouldRenderEditAddressButton={shouldRenderEditAddressButton} />
            )}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress} />}
            {screenReaderNotification}
        </Module>
    );
};

export default AccountManagementAddressView;
